import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, MapPin, Plus, Pencil, Trash2, Users, Map, Globe, 
  Loader2, AlertCircle, CheckCircle, BarChart3, Mail, Phone
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import RecordActions from '../../components/RecordActions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ Sub-Components ============

const AreaCard = ({ area, isSelected, onSelect }) => (
  <motion.div
    key={area.id}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <Card
      className={`cursor-pointer transition-all ${
        isSelected ? 'ring-2 ring-[#F56F05] border-[#F56F05]' : 'hover:shadow-md'
      }`}
      onClick={() => onSelect(area)}
      data-testid={`area-card-${area.city_name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              area.is_active ? 'bg-green-100' : 'bg-zinc-100'
            }`}>
              <MapPin className={`h-5 w-5 ${area.is_active ? 'text-green-600' : 'text-zinc-400'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900">{area.city_name}</h3>
              <p className="text-sm text-zinc-500">{area.state}</p>
            </div>
          </div>
          <Badge variant={area.is_active ? 'default' : 'secondary'}>
            {area.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="mt-3 flex items-center gap-4 text-sm text-zinc-600">
          <span className="flex items-center gap-1">
            <Map className="h-4 w-4" /> {area.zip_count || 0} ZIPs
          </span>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const WaitlistStatsCards = ({ stats }) => (
  <div className="grid sm:grid-cols-3 gap-4">
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <Users className="h-5 w-5 text-[#F56F05]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-zinc-900">{stats?.total_requests || 0}</p>
            <p className="text-sm text-zinc-500">Total Requests</p>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <MapPin className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-zinc-900">{stats?.by_zip?.length || 0}</p>
            <p className="text-sm text-zinc-500">Unique ZIPs Requested</p>
          </div>
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-zinc-900">
              {stats?.by_zip?.[0]?.zip_code || 'N/A'}
            </p>
            <p className="text-sm text-zinc-500">Most Requested ZIP</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const DemandByZipCard = ({ stats }) => {
  if (!stats?.by_zip?.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Demand by ZIP Code</CardTitle>
        <CardDescription>Areas with the highest expansion interest</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {stats.by_zip.slice(0, 10).map((item) => (
            <div key={item.zip_code} className="flex items-center gap-4">
              <span className="text-sm font-mono w-16">{item.zip_code}</span>
              <div className="flex-1 h-6 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#F56F05] to-orange-400 rounded-full"
                  style={{ width: `${(item.count / stats.by_zip[0].count) * 100}%` }}
                />
              </div>
              <span className="text-sm font-semibold w-8">{item.count}</span>
              {item.city && <span className="text-sm text-zinc-500">{item.city}</span>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const RecentWaitlistList = ({ requests, headers, onChanged }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">Recent Requests</CardTitle>
      <CardDescription>Latest people who joined the expansion waitlist</CardDescription>
    </CardHeader>
    <CardContent>
      {requests.length === 0 ? (
        <div className="text-center py-8">
          <Users className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500">No waitlist requests yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.slice(0, 20).map((req) => (
            <div key={req.id} className="flex items-center gap-4 p-3 bg-zinc-50 rounded-lg" data-testid={`waitlist-row-${req.id}`}>
              <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-600 font-semibold">
                {req.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-zinc-900 truncate">{req.name}</p>
                <div className="flex items-center gap-3 text-sm text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {req.email}
                  </span>
                  {req.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {req.phone}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <Badge variant="outline" className="font-mono">{req.zip_code}</Badge>
                {req.city && <p className="text-xs text-zinc-400 mt-1">{req.city}</p>}
              </div>
              <RecordActions
                canEdit={false}
                canArchive={false}
                canDelete={true}
                deleteUrl={`/api/admin/waitlist/${req.id}`}
                deleteConfirmTitle="Remove waitlist request?"
                deleteConfirmDescription="Permanently deletes this waitlist signup. The customer will no longer receive expansion notifications for their ZIP. Audit-logged."
                onDeleted={onChanged}
                headers={headers}
                testIdPrefix="waitlist"
                entityId={req.id}
              />
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const ZipGrid = ({ zips, onDeleteZip }) => {
  if (zips.length === 0) {
    return (
      <div className="text-center py-8 bg-zinc-50 rounded-lg">
        <MapPin className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
        <p className="text-zinc-500">No ZIP codes yet</p>
        <p className="text-sm text-zinc-400">Add ZIP codes to enable booking in this area</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-80 overflow-y-auto">
      {zips.map((zip) => (
        <div
          key={zip.id}
          className={`group relative px-2 py-1 rounded text-sm font-mono text-center ${
            zip.is_active ? 'bg-green-50 text-green-700' : 'bg-zinc-100 text-zinc-500'
          }`}
        >
          {zip.zip_code}
          <button
            onClick={(e) => { e.stopPropagation(); onDeleteZip(zip.id); }}
            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-xs flex items-center justify-center"
          >
            x
          </button>
        </div>
      ))}
    </div>
  );
};

const AdminServiceAreasPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [waitlistStats, setWaitlistStats] = useState(null);
  const [waitlistRequests, setWaitlistRequests] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaZips, setAreaZips] = useState([]);
  const [showAddArea, setShowAddArea] = useState(false);
  const [showBulkZips, setShowBulkZips] = useState(false);
  const [newAreaForm, setNewAreaForm] = useState({ city_name: '', state: '', default_timezone: 'America/Los_Angeles', is_active: true });
  const [bulkZipsText, setBulkZipsText] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [areasRes, statsRes, waitlistRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/service-areas`, { headers }),
        axios.get(`${API_URL}/api/admin/waitlist/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/waitlist`, { headers })
      ]);
      setServiceAreas(areasRes.data);
      setWaitlistStats(statsRes.data);
      setWaitlistRequests(waitlistRes.data);
    } catch (_) {
      toast.error('Failed to load service areas');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchAreaZips = async (areaId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/service-areas/${areaId}/zips`, { headers });
      setAreaZips(res.data);
    } catch (err) {
      toast.error('Failed to load ZIP codes');
    }
  };

  const handleSelectArea = async (area) => {
    setSelectedArea(area);
    await fetchAreaZips(area.id);
  };

  const handleCreateArea = async () => {
    if (!newAreaForm.city_name || !newAreaForm.state) {
      toast.error('City name and state are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/service-areas`, newAreaForm, { headers });
      toast.success('Service area created');
      setShowAddArea(false);
      setNewAreaForm({ city_name: '', state: '', default_timezone: 'America/Los_Angeles', is_active: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create area');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAreaActive = async (area) => {
    try {
      await axios.put(`${API_URL}/api/admin/service-areas/${area.id}`, { is_active: !area.is_active }, { headers });
      toast.success(`${area.city_name} ${area.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update area');
    }
  };

  const handleDeleteArea = async (area) => {
    if (!window.confirm(`Delete ${area.city_name}? This will also remove all associated ZIP codes.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/service-areas/${area.id}`, { headers });
      toast.success('Service area deleted');
      if (selectedArea?.id === area.id) {
        setSelectedArea(null);
        setAreaZips([]);
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to delete area');
    }
  };

  const handleAddBulkZips = async () => {
    if (!selectedArea) return;
    const zips = bulkZipsText.split(/[\s,\n]+/).filter(z => z.trim());
    if (zips.length === 0) {
      toast.error('Enter at least one ZIP code');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/service-areas/${selectedArea.id}/zips/bulk`, zips, { headers });
      toast.success(`Added ${res.data.added_count} ZIP codes. ${res.data.skipped_count} skipped (duplicates).`);
      setShowBulkZips(false);
      setBulkZipsText('');
      await fetchAreaZips(selectedArea.id);
      fetchData();
    } catch (err) {
      toast.error('Failed to add ZIP codes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteZip = async (zipId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/service-areas/zips/${zipId}`, { headers });
      toast.success('ZIP code removed');
      await fetchAreaZips(selectedArea.id);
      fetchData();
    } catch (err) {
      toast.error('Failed to remove ZIP');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-zinc-900">Service Areas & Expansion</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="areas" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="areas" data-testid="tab-areas">
              <Map className="h-4 w-4 mr-2" /> Service Areas
            </TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">
              <Users className="h-4 w-4 mr-2" /> Expansion Waitlist
              {waitlistStats?.total_requests > 0 && (
                <Badge className="ml-2 bg-[#F56F05]">{waitlistStats.total_requests}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Service Areas Tab */}
          <TabsContent value="areas" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Areas List */}
              <div className="lg:col-span-1 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-zinc-900">Service Areas</h2>
                  <Dialog open={showAddArea} onOpenChange={setShowAddArea}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[#F56F05] hover:bg-[#C2410C]" data-testid="add-area-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add Area
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Service Area</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label>City/Region Name *</Label>
                          <Input
                            value={newAreaForm.city_name}
                            onChange={(e) => setNewAreaForm({ ...newAreaForm, city_name: e.target.value })}
                            placeholder="Portland Metro"
                            data-testid="new-area-name"
                          />
                        </div>
                        <div>
                          <Label>State(s) *</Label>
                          <Input
                            value={newAreaForm.state}
                            onChange={(e) => setNewAreaForm({ ...newAreaForm, state: e.target.value })}
                            placeholder="OR/WA"
                            data-testid="new-area-state"
                          />
                        </div>
                        <div>
                          <Label>Timezone</Label>
                          <Input
                            value={newAreaForm.default_timezone}
                            onChange={(e) => setNewAreaForm({ ...newAreaForm, default_timezone: e.target.value })}
                            placeholder="America/Los_Angeles"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={newAreaForm.is_active}
                            onCheckedChange={(v) => setNewAreaForm({ ...newAreaForm, is_active: v })}
                          />
                          <Label>Active</Label>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setShowAddArea(false)}>Cancel</Button>
                          <Button onClick={handleCreateArea} disabled={saving} className="bg-[#F56F05]" data-testid="save-area-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                            Create
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {serviceAreas.length === 0 ? (
                  <Card className="bg-zinc-50 border-dashed">
                    <CardContent className="py-8 text-center">
                      <Globe className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
                      <p className="text-zinc-500">No service areas yet</p>
                      <p className="text-sm text-zinc-400">Create your first service area to get started</p>
                    </CardContent>
                  </Card>
                ) : (
                  serviceAreas.map((area) => (
                    <AreaCard
                      key={area.id}
                      area={area}
                      isSelected={selectedArea?.id === area.id}
                      onSelect={handleSelectArea}
                    />
                  ))
                )}
              </div>

              {/* Area Detail / ZIP Management */}
              <div className="lg:col-span-2">
                {selectedArea ? (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <div>
                        <CardTitle>{selectedArea.city_name}</CardTitle>
                        <CardDescription>{selectedArea.state} • {selectedArea.default_timezone}</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleAreaActive(selectedArea)}
                          data-testid="toggle-area-btn"
                        >
                          {selectedArea.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteArea(selectedArea)}
                          data-testid="delete-area-btn"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-zinc-700">ZIP Codes ({areaZips.length})</h4>
                        <Dialog open={showBulkZips} onOpenChange={setShowBulkZips}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid="add-zips-btn">
                              <Plus className="h-4 w-4 mr-1" /> Add ZIPs
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Add ZIP Codes to {selectedArea.city_name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-4">
                              <div>
                                <Label>ZIP Codes (one per line or comma-separated)</Label>
                                <Textarea
                                  value={bulkZipsText}
                                  onChange={(e) => setBulkZipsText(e.target.value)}
                                  placeholder="97201\n97202\n97203\nor 97201, 97202, 97203"
                                  rows={8}
                                  data-testid="bulk-zips-textarea"
                                />
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setShowBulkZips(false)}>Cancel</Button>
                                <Button onClick={handleAddBulkZips} disabled={saving} className="bg-[#F56F05]" data-testid="save-zips-btn">
                                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                                  Add ZIPs
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>

                      {areaZips.length === 0 ? (
                        <div className="text-center py-8 bg-zinc-50 rounded-lg">
                          <MapPin className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
                          <p className="text-zinc-500">No ZIP codes yet</p>
                          <p className="text-sm text-zinc-400">Add ZIP codes to enable booking in this area</p>
                        </div>
                      ) : (
                        <ZipGrid zips={areaZips} onDeleteZip={handleDeleteZip} />
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-zinc-50 border-dashed">
                    <CardContent className="py-16 text-center">
                      <Map className="h-12 w-12 mx-auto text-zinc-300 mb-4" />
                      <p className="text-zinc-500 font-medium">Select a service area</p>
                      <p className="text-sm text-zinc-400">Click on an area to manage its ZIP codes</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist" className="space-y-6">
            <WaitlistStatsCards stats={waitlistStats} />
            <DemandByZipCard stats={waitlistStats} />
            <RecentWaitlistList requests={waitlistRequests} headers={headers} onChanged={fetchData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminServiceAreasPage;
